<script setup>
import { computed, ref, nextTick, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CreateAssistantDialog from 'dashboard/components-next/captain/pageComponents/assistant/CreateAssistantDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import AssistantCard from 'dashboard/components-next/captain/assistant/AssistantCard.vue';
import AssistantPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/AssistantPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';

const { isOnChatwootCloud } = useAccount();

const store = useStore();
const route = useRoute();
const router = useRouter();

const dialogType = ref('');
const selectedAssistant = ref(null);
const createAssistantDialog = ref(null);
const deleteAssistantDialog = ref(null);

const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const isEmpty = computed(() => !assistants.value.length);

const openAssistantRoute = (name, assistantId) =>
  router.push({
    name,
    params: { accountId: route.params.accountId, assistantId },
  });

const handleCreate = () => {
  router.push({
    name: 'captain_assistants_new',
    params: { accountId: route.params.accountId },
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedAssistant.value = null;
};

const handleAction = ({ action, id }) => {
  selectedAssistant.value = assistants.value.find(
    assistant => assistant.id === id
  );

  if (action === 'viewConnectedInboxes') {
    openAssistantRoute('captain_assistants_inboxes_index', id);
    return;
  }

  if (action === 'edit') {
    dialogType.value = 'edit';
    nextTick(() => createAssistantDialog.value.dialogRef.open());
    return;
  }

  nextTick(() => deleteAssistantDialog.value.dialogRef.open());
};

onMounted(() => store.dispatch('captainAssistants/get'));
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.HEADER')"
    :header-subtitle="$t('CAPTAIN.ASSISTANTS.SUBTITLE')"
    :button-label="$t('CAPTAIN.ASSISTANTS.ADD_NEW')"
    :button-policy="['administrator']"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
    :is-fetching="isFetching"
    :is-empty="isEmpty"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @click="handleCreate"
  >
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('CAPTAIN.HEADER_KNOW_MORE')"
        :title="$t('CAPTAIN.ASSISTANTS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('CAPTAIN.ASSISTANTS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnChatwootCloud"
        fallback-thumbnail="/assets/images/dashboard/captain/assistant-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/captain/assistant-popover-dark.svg"
      />
    </template>

    <template #emptyState>
      <AssistantPageEmptyState @click="handleCreate" />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #body>
      <div class="grid grid-cols-1 gap-4 p-px md:grid-cols-2">
        <AssistantCard
          v-for="assistant in assistants"
          :id="assistant.id"
          :key="assistant.id"
          :name="assistant.name"
          :description="assistant.description"
          :updated-at="assistant.updated_at"
          @open="
            openAssistantRoute('captain_assistants_overview_index', $event)
          "
          @action="handleAction"
        />
      </div>
    </template>

    <CreateAssistantDialog
      v-if="dialogType"
      ref="createAssistantDialog"
      :type="dialogType"
      :selected-assistant="selectedAssistant"
      @close="handleCreateClose"
    />

    <DeleteDialog
      v-if="selectedAssistant"
      ref="deleteAssistantDialog"
      type="Assistants"
      translation-key="ASSISTANTS"
      :entity="selectedAssistant"
    />
  </PageLayout>
</template>
